import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { initPosDataSource, posDataMode } from './index';
import type { PosDataSource } from './types';

/**
 * The gate between the POS pages and the data seam: nothing renders the till
 * until the source is ready (in local mode, until the database has opened,
 * migrated and - on first run - provisioned). In api mode this resolves
 * immediately and the pages behave exactly as before Phase 3.
 */
export function usePosDataSource(): {
  source: PosDataSource | null;
  isLoading: boolean;
  error: unknown;
  retry: () => void;
} {
  const { session } = useAuth();
  const query = useQuery({
    queryKey: ['pos-data-source', posDataMode(session?.user.id), session?.user.id ?? null],
    queryFn: () => {
      if (!session) throw new Error('Sign in first');
      return initPosDataSource(session);
    },
    enabled: Boolean(session),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
  return {
    source: query.data ?? null,
    isLoading: query.isPending,
    error: query.error,
    retry: () => void query.refetch(),
  };
}
